import React, { useState, Suspense } from 'react';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';

import { useHistory, useRouteMatch } from 'react-router';
import { Paper, Button } from '../Common/Components';
import SuccessIcon from '../../assets/images/complete.svg';
import ErrorIcon from '../../assets/images/error.svg';
import { useAuth } from '../../hooks/useAuth';
import { verifyBarcode } from '../../services/c2dcrm';
import { ROUTES } from '../../constants/route';

// const BarcodeScanner = React.lazy(() => import('react-qr-barcode-scanner'));
import BarcodeScannerComponent from "react-qr-barcode-scanner";

type StateType = {
  code: string;
  loading: boolean;
  success?: boolean;
};

type FeedbackProps = {
  title: string;
  description: string;
  icon: string;
  altText: string;
  buttonText: string;
  onClick?: () => void;
};

const initialState: StateType = {
  code: '',
  loading: false,
};

const useStyles = makeStyles({
  container: {
    display: 'grid',
    gridGap: '16px',
    placeItems: 'center',

    // Video height in Safari is larger than other browsers.
    '& video': {
      maxHeight: '360px',
      height: 'auto',
    },
  },
  image: {
    display: 'grid',
    placeItems: 'center',
  },
});

const Scanfeedback: React.FC<FeedbackProps> = ({
  title,
  description,
  icon,
  altText,
  buttonText,
  onClick,
}) => {
  const classes = useStyles();

  return (
    <>
      <Typography variant="h1" color="primary">
        {title}
      </Typography>
      <Typography>{description}</Typography>

      <div className={classes.image}>
        <img src={icon} alt={altText} />
      </div>

      <Button className="wide" onClick={onClick}>
        {buttonText}
      </Button>
    </>
  );
};

const Scanner: React.FC = () => {
  const match = useRouteMatch<{ dataPassId: string }>(ROUTES.MERCHANT_SCANNER);
  const history = useHistory();
  const classes = useStyles();
  const [state, setState] = useState<StateType>(initialState);
  const { token } = useAuth();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onScan = async (_: unknown, result?: any) => {
    if (result) {
      if (!token) return;

      setState({ ...state, loading: true });

      try {
        if (!match?.params?.dataPassId) return;
        const response = await verifyBarcode(match.params.dataPassId, result.text, token);
        setState({ code: result.text, loading: false, success: response?.data?.verified || false });
      } catch (error) {
        setState({ code: '', loading: false, success: false });
      }
    }
  };

  const onError = () => setState({ code: '', loading: false, success: false });

  const renderComponent = () => {
    switch (state.success) {
      case true:
        return (
          <Scanfeedback
            title="Scan Successful"
            description="The user has been found, verified and logged."
            icon={SuccessIcon}
            altText="Success"
            buttonText="Scan next code"
            onClick={() => setState(initialState)}
          />
        );
      case false:
        return (
          <Scanfeedback
            title="Unfortunately there was an error"
            description="Please try again."
            icon={ErrorIcon}
            altText="Error"
            buttonText="Scan Code"
            onClick={() => setState(initialState)}
          />
        );
      default:
        return state.loading ? (
          <CircularProgress />
        ) : (
          <Suspense fallback={<div>Loading camera</div>}>
            <BarcodeScannerComponent
              width={500}
              height={500}
              onUpdate={onScan}
              onError={onError}
            />
          </Suspense>
        );
    }
  };

  return (
    <Paper className={classes.container}>
      {renderComponent()}
      <Button
        className="text wide"
        onClick={() => history.push(`/data-pass/${match?.params?.dataPassId}`)}
      >
        Go Back
      </Button>
    </Paper>
  );
};

export default Scanner;
